<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useKeysStore } from '@/stores/keys'
import { useUserStore } from '@/stores/user'
import { useSwitchStore } from '@/stores/switch'
import { useAutoRefresh } from '@/composables/useAutoRefresh'
import { collectHealthServerIds, useServerHealthStore } from '@/stores/serverHealth'
import KeyGroupCard from '@/components/KeyGroupCard.vue'
import SubscriptionKeysTable from '@/components/keys/SubscriptionKeysTable.vue'
import SubscriptionWidget from '@/components/dashboard/SubscriptionWidget.vue'
import ActiveServersWidget from '@/components/dashboard/ActiveServersWidget.vue'
import BuyCodeWidget from '@/components/dashboard/BuyCodeWidget.vue'
import SwitchQuotaWidget from '@/components/dashboard/SwitchQuotaWidget.vue'
import SwitchTestBanner from '@/components/dashboard/SwitchTestBanner.vue'
import SwitchTestGroupCard from '@/components/switch/SwitchTestGroupCard.vue'
import SubscriptionLinkCard from '@/components/keys/SubscriptionLinkCard.vue'
import GlobalFingerprintCard from '@/components/dashboard/GlobalFingerprintCard.vue'
import RebuildKeysCard from '@/components/dashboard/RebuildKeysCard.vue'
import RussiaConnectCard from '@/components/dashboard/RussiaConnectCard.vue'
import AvailableServersGrid from '@/components/dashboard/AvailableServersGrid.vue'
import { groupManualKeys, isManualGroupExpired } from '@/utils/keys'
import { findTestForGroup } from '@/utils/switch'

const store = useKeysStore()
const user = useUserStore()
const switchStore = useSwitchStore()
const serverHealth = useServerHealthStore()

const hideExpiredManual = ref(true)

const manualKeys = computed(() => store.items.filter((k) => !k.is_subscription_key))
const manualGroups = computed(() => groupManualKeys(manualKeys.value))
const visibleManualGroups = computed(() =>
  hideExpiredManual.value
    ? manualGroups.value.filter((group) => !isManualGroupExpired(group))
    : manualGroups.value,
)
const subKeys = computed(() => store.items.filter((k) => k.is_subscription_key))
const showTopWidgets = computed(() =>
  store.items.length > 0
  && (manualKeys.value.length > 0 || user.subscriptionActive || user.profile?.subscription_expires_at),
)
const showSubLink = computed(() => store.items.length > 0)
const bannerTests = computed(() =>
  switchStore.activeTestList.filter(
    (test) => !visibleManualGroups.value.some((group) => findTestForGroup(group, [test])),
  ),
)

const healthServerIds = computed(() => collectHealthServerIds(store.items))

watch(healthServerIds, (ids) => {
  serverHealth.startPolling(ids)
}, { immediate: true })

onUnmounted(() => serverHealth.stopPolling())

async function bootstrap() {
  await Promise.all([user.fetch(true), store.fetch()])
  await switchStore.poll()
}

onMounted(() => void bootstrap())
useAutoRefresh(async () => {
  await store.fetch(true)
  await switchStore.poll()
}, 30_000)

async function refresh() {
  await Promise.all([store.fetch(true), user.fetch(true)])
  await switchStore.poll()
}

</script>

<template>
  <div>
    <div class="mb-6 flex items-center justify-between gap-4">
      <h1 class="page-title">{{ $t('dashboard.title') }}</h1>
      <div class="flex gap-2">
        <button
          type="button"
          class="btn btn-sm btn-secondary"
          :disabled="store.loading"
          @click="refresh"
        >
          {{ store.loading ? $t('dashboard.loading') : $t('dashboard.refresh') }}
        </button>
      </div>
    </div>

    <div v-if="store.error" class="alert alert-danger mb-4">
      {{ store.error }}
    </div>

    <SwitchTestBanner
      v-if="bannerTests.length || Object.keys(switchStore.hungMessages).length"
      :tests="bannerTests"
    />

    <div
      v-if="!store.loading || store.items.length > 0"
      class="mb-4 flex flex-wrap gap-3"
    >
      <SwitchQuotaWidget v-if="manualKeys.length > 0 && user.serverSwitchEnabled" />
      <ActiveServersWidget :keys="store.items" />
      <SubscriptionWidget v-if="showTopWidgets" />
      <BuyCodeWidget />
    </div>

    <SubscriptionLinkCard v-if="showSubLink" />

    <div
      v-if="user.profile && store.items.length > 0"
      class="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      <GlobalFingerprintCard />
      <RussiaConnectCard />
      <RebuildKeysCard />
    </div>

    <div
      v-if="store.loading && store.items.length === 0"
      class="flex flex-col items-center justify-center py-20 text-muted"
    >
      <span class="animate-[spin_2s_linear_infinite] border-8 border-l-primary border-r-primary rounded-full w-14 h-14 inline-block align-middle m-auto mb-10"></span>
      <span>{{ $t('dashboard.loading') }}</span>
    </div>

    <div
      v-else-if="store.items.length === 0"
      class="panel border border-dashed border-white-light py-16 text-center"
    >
      <div class="mb-2 text-lg font-bold">{{ $t('dashboard.empty') }}</div>
      <div class="mb-6 text-sm text-muted">{{ $t('dashboard.empty_hint') }}</div>
      <div class="flex justify-center">
        <RouterLink to="/activate" class="btn btn-primary">
          {{ $t('activate.title') }}
        </RouterLink>
      </div>
    </div>

    <template v-else>
      <section v-if="manualGroups.length" class="mb-8">
        <div class="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <h2 class="text-base font-bold">
            Свободные ключи
          </h2>
          <label class="inline-flex cursor-pointer items-center gap-2 text-sm font-normal text-muted">
            <input
              v-model="hideExpiredManual"
              type="checkbox"
              class="h-4 w-4 rounded border-white-light text-primary"
            />
            Скрыть истекшие ключи
          </label>
        </div>
        <div v-if="visibleManualGroups.length === 0" class="panel text-sm text-muted">
          Все свободные ключи истекли. Снимите галочку, чтобы показать их.
        </div>
        <div v-else class="space-y-3">
          <template v-for="group in visibleManualGroups" :key="group[0]?.group_key || group[0]?.id">
            <SwitchTestGroupCard
              v-if="findTestForGroup(group, switchStore.activeTestList)"
              :keys="group"
              :test="findTestForGroup(group, switchStore.activeTestList)!"
            />
            <KeyGroupCard v-else :keys="group" />
          </template>
        </div>
      </section>

      <AvailableServersGrid
        v-if="manualKeys.length > 0 && subKeys.length === 0"
        :manual-keys="manualKeys"
      />

      <section v-if="subKeys.length">
        <h2 class="mb-3 text-base font-bold">
          Ключи по подписке
        </h2>
        <SubscriptionKeysTable :keys="subKeys" />
      </section>
    </template>
  </div>
</template>
